import React, { FunctionComponent, ReactElement } from 'react';

import styles from './FullCalendarBlock.module.scss';

import FullCalendar from '@fullcalendar/react'; // must go before plugins
import dayGridPlugin from '@fullcalendar/daygrid'; // a plugin!

import { Tooltip, Typography } from '@material-ui/core';
import DotsMenu from 'app/components/DotsMenu';
import { ExpandIcon } from 'app/assets/images/icons';
import { format } from 'date-fns';

import CardTitle from 'app/components/CardTitle';
import { _t } from 'app/utils/i18n';

import { constants, getFromLocalStorage } from 'app/utils/localStorage';
import { useQuery } from 'react-query';
import { loadAuthorCalendarData, loadCalendarData } from 'influencers/Requests';
import Loader from 'app/components/Loader';
import ServerError from 'app/components/ErrorBoundary/ServerError';

interface Props {
  id: any;
  slug: string;
  title?: string;
  setActiveTab?: any;
  setOpenTab?: any;
  monthCalendar?: boolean;
  hideOtherMonths?: boolean;
  initialDate?: string;
  compId: string;
  isInfluencerModule?: boolean;
  counter?: number | string;
  startDate?: any;
  endDate?: any;
  author?: boolean | undefined;
  additionalComponents?: ReactElement;
  isReport?: boolean;
  isTemplate?: boolean;
  upToDate?: any;
  customFilters?: any;
}

const FullCalendarBlock: FunctionComponent<Props> = ({
  compId,
  id,
  slug,
  title,
  setActiveTab,
  setOpenTab,
  monthCalendar,
  hideOtherMonths,
  initialDate,
  isInfluencerModule,
  counter,
  startDate,
  endDate,
  author,
  additionalComponents,
  isReport,
  isTemplate,
  upToDate,
  customFilters,
}) => {
  const currentLanguage = getFromLocalStorage(constants.language);

  const queryObj = author
    ? { body: { influencer_id: Number(id), ...customFilters }, slug }
    : {
        body: {
          id: Number(id),
          start_date: startDate,
          end_date: endDate,
          ...customFilters,
        },
        isInfluencerModule,
        slug,
      };

  const { data, isLoading, isError } = useQuery(
    ['loadCalendarData', queryObj],
    author ? loadAuthorCalendarData : loadCalendarData,
    { enabled: counter === 0 ? false : true },
  );
  const renderInnerContent = (innerProps: any) => {
    return (
      <div className="fc-event-main-frame">
        {innerProps.timeText && (
          <div className="fc-event-time">{innerProps.timeText}</div>
        )}
        <div className="fc-event-title-container">
          <div className="fc-event-title fc-sticky">
            {innerProps.event.title || <>&nbsp;</>}
          </div>
        </div>
      </div>
    );
  };
  if (isLoading) return <Loader />;
  return (
    <ServerError requiredApiCalls={[isError]}>
      <div id={compId ? compId : ''}>
        {title && (
          <CardTitle title={title} compId={compId} hideIcon={isReport} />
        )}
        <div
          className={`${styles.FullCalendarBlock} ${
            monthCalendar && styles.monthCalendar
          } `}
        >
          <div className={styles.Actions}>
            {!isReport && !isTemplate && (
              <div className={styles.ActionBtn}>
                <ExpandIcon
                  onClick={() => {
                    window.scrollTo(0, 0);
                    setActiveTab(1);
                    setOpenTab(1);
                  }}
                />
              </div>
            )}
            <div className={styles.ActionBtn}>
              {!isReport && <DotsMenu id={compId} />}
            </div>
          </div>
          {initialDate && (
            <div className={styles.date_counter}>
              <span
                className="counter-date cursor-pointer"
                onClick={() => {
                  upToDate(initialDate?.split('-')?.slice(0, 2)?.join('-'));
                }}
              >
                {format(new Date(initialDate), 'yyyy/MM')}
              </span>
              <span>{counter}</span>
            </div>
          )}
          <FullCalendar
            locale={currentLanguage}
            plugins={[dayGridPlugin]}
            weekNumbers={false}
            dayHeaderFormat={{
              weekday: 'narrow',
            }}
            initialDate={initialDate ? initialDate : new Date()}
            height="auto"
            showNonCurrentDates={hideOtherMonths ? false : true}
            buttonText={{
              prev: _t('basic:prev'),
              next: _t('basic:next'),
            }}
            headerToolbar={{
              start: 'prev', // will normally be on the left. if RTL, will be on the right
              center: 'title',
              end: 'next', // will normally be on the right. if RTL, will be on the left
            }}
            events={data as []}
            eventContent={(arg) => {
              return (
                <Tooltip
                  title={
                    <Typography color="inherit">
                      {arg.event.extendedProps.value}
                    </Typography>
                  }
                  arrow
                  className="cursor-pointer"
                  onClick={() => {
                    upToDate(arg.event.startStr, true);
                    setOpenTab(0);
                  }}
                >
                  {renderInnerContent(arg)}
                </Tooltip>
              );
            }}
          />
        </div>
        <div className="position-relative">{additionalComponents}</div>
      </div>
    </ServerError>
  );
};

export default FullCalendarBlock;
